import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { adminAPI } from '../services/api';
import {
  Shield,
  Users,
  Package,
  DollarSign,
  TrendingUp,
  Store,
  Calendar,
  UserCheck,
  UserX,
  Clock,
  Check,
  X,
} from 'lucide-react';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchAdminData();
  }, [user, navigate]);

  const fetchAdminData = async () => {
    try {
      const [statsResponse, dealersResponse, pendingResponse] = await Promise.all([
        api.get('/admin/stats/overview'),
        api.get('/admin/dealers'),
        adminAPI.getPendingUsers().catch(() => ({ data: { users: [] } })),
      ]);

      setStats(statsResponse.data);
      setDealers(dealersResponse.data.dealers);
      setPendingUsers(pendingResponse.data?.users ?? []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDealer = async (dealerId) => {
    try {
      await api.patch(`/admin/dealers/${dealerId}/toggle-active`);
      fetchAdminData();
    } catch (error) {
      console.error('Error toggling dealer:', error);
      alert('Failed to toggle dealer status');
    }
  };

  const fetchPendingUsers = async () => {
    setPendingLoading(true);
    try {
      const res = await adminAPI.getPendingUsers();
      setPendingUsers(res.data?.users ?? []);
    } catch (err) {
      console.error('Error fetching pending users:', err);
      setPendingUsers([]);
    } finally {
      setPendingLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await adminAPI.approveUser(userId);
      fetchPendingUsers();
      fetchAdminData();
    } catch (err) {
      console.error('Error approving user:', err);
      alert(err.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Reject this user? They will be deactivated and cannot sign in.')) return;
    try {
      await adminAPI.rejectUser(userId);
      fetchPendingUsers();
      fetchAdminData();
    } catch (err) {
      console.error('Error rejecting user:', err);
      alert(err.response?.data?.detail || 'Failed to reject user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Shield className="w-10 h-10 text-red-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Super Admin Dashboard</h1>
          <p className="text-gray-600">System-wide analytics and management</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'overview'
              ? 'border-b-2 border-slate-900 text-slate-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('dealers')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'dealers'
              ? 'border-b-2 border-slate-900 text-slate-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Dealers
        </button>
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-6 py-3 font-semibold transition-all flex items-center gap-2 ${
            activeTab === 'pending'
              ? 'border-b-2 border-slate-900 text-slate-900'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Clock className="w-4 h-4" />
          Pending
          {pendingUsers.length > 0 && (
            <span className="bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {pendingUsers.length}
            </span>
          )}
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* User Stats */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6" />
              Dealers
            </h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-100">
                <p className="text-sm text-gray-600 mb-1">Total Dealers</p>
                <p className="text-4xl font-bold text-blue-600">{stats.users.total_dealers}</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-100">
                <p className="text-sm text-gray-600 mb-1">Active Dealers</p>
                <p className="text-4xl font-bold text-green-600">{stats.users.active_dealers}</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-6 border-2 border-purple-100">
                <p className="text-sm text-gray-600 mb-1">New (30 days)</p>
                <p className="text-4xl font-bold text-purple-600">{stats.users.new_dealers_30d}</p>
              </div>
            </div>
          </div>

          {/* Inventory Stats */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Package className="w-6 h-6" />
              Inventory
            </h2>
            <div className="grid grid-cols-5 gap-4">
              <div className="bg-gradient-to-br from-slate-50 to-stone-50 rounded-2xl p-4 border-2 border-slate-100">
                <p className="text-sm text-gray-600 mb-1">Total Watches</p>
                <p className="text-3xl font-bold text-gray-900">{stats.inventory.total_watches}</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 border-2 border-green-100">
                <p className="text-sm text-gray-600 mb-1">Available</p>
                <p className="text-3xl font-bold text-green-600">{stats.inventory.available_watches}</p>
              </div>
              <div className="bg-gradient-to-br from-red-50 to-rose-50 rounded-2xl p-4 border-2 border-red-100">
                <p className="text-sm text-gray-600 mb-1">Sold</p>
                <p className="text-3xl font-bold text-red-600">{stats.inventory.sold_watches}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 border-2 border-blue-100">
                <p className="text-sm text-gray-600 mb-1">Public</p>
                <p className="text-3xl font-bold text-blue-600">{stats.inventory.public_watches}</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 border-2 border-amber-100">
                <p className="text-sm text-gray-600 mb-1">Total Value</p>
                <p className="text-2xl font-bold text-orange-600">
                  ${(Number(stats.inventory?.total_inventory_value) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          </div>

          {/* Sales Stats */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-6 h-6" />
              Sales
            </h2>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border-2 border-emerald-100">
                <p className="text-sm text-gray-600 mb-1">Total Sales</p>
                <p className="text-4xl font-bold text-emerald-600">{stats.sales.total_sales}</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-lime-50 rounded-2xl p-6 border-2 border-green-100">
                <p className="text-sm text-gray-600 mb-1">Total Revenue</p>
                <p className="text-3xl font-bold text-green-600">
                  ${(Number(stats.sales?.total_revenue) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="bg-gradient-to-br from-cyan-50 to-sky-50 rounded-2xl p-6 border-2 border-cyan-100">
                <p className="text-sm text-gray-600 mb-1">Sales (30d)</p>
                <p className="text-4xl font-bold text-cyan-600">{stats.sales.sales_30d}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-100">
                <p className="text-sm text-gray-600 mb-1">Revenue (30d)</p>
                <p className="text-3xl font-bold text-blue-600">
                  ${(Number(stats.sales?.revenue_30d) || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          </div>

          {/* Other Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-6 border-2 border-violet-100">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-6 h-6 text-violet-600" />
                <p className="text-lg font-semibold text-gray-900">Total Clients</p>
              </div>
              <p className="text-4xl font-bold text-violet-600">{stats.clients.total_clients}</p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-6 border-2 border-pink-100">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-6 h-6 text-pink-600" />
                <p className="text-lg font-semibold text-gray-900">Total Services</p>
              </div>
              <p className="text-4xl font-bold text-pink-600">{stats.services.total_services}</p>
            </div>
          </div>
        </div>
      )}

      {/* Pending Tab */}
      {activeTab === 'pending' && (
        <div className="space-y-4">
          <div className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 overflow-hidden">
            {pendingLoading ? (
              <div className="p-12 flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-900 border-t-transparent" />
              </div>
            ) : pendingUsers.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                No users pending approval.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Company</th>
                      <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Joined</th>
                      <th className="px-6 py-4 text-center text-sm font-bold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {pendingUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-slate-50">
                        <td className="px-6 py-4 font-medium text-gray-900">{u.dealer_name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{u.company_name || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleApprove(u.id)}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-100 text-green-800 font-semibold hover:bg-green-200 transition-colors"
                            >
                              <Check className="w-4 h-4" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleReject(u.id)}
                              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-100 text-red-800 font-semibold hover:bg-red-200 transition-colors"
                            >
                              <X className="w-4 h-4" />
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dealers Tab */}
      {activeTab === 'dealers' && (
        <div className="space-y-4">
          <div className="bg-white rounded-3xl shadow-xl border-2 border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Dealer</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Location</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900">Watches</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900">Clients</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900">Sales</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">Joined</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900">Status</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dealers.map((dealer) => (
                    <tr key={dealer.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-bold text-gray-900">{dealer.dealer_name}</div>
                          {dealer.company_name && (
                            <div className="text-sm text-gray-500">{dealer.company_name}</div>
                          )}
                          {dealer.is_admin && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full mt-1">
                              <Shield className="w-3 h-3" />
                              ADMIN
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{dealer.email}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {[dealer.city, dealer.state, dealer.country].filter(Boolean).join(', ') || '-'}
                      </td>
                      <td className="px-6 py-4 text-center font-semibold text-gray-900">
                        {dealer.stats.watches}
                      </td>
                      <td className="px-6 py-4 text-center font-semibold text-gray-900">
                        {dealer.stats.clients}
                      </td>
                      <td className="px-6 py-4 text-center font-semibold text-gray-900">
                        {dealer.stats.sales}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(dealer.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold ${
                          dealer.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {dealer.is_active ? <UserCheck className="w-3 h-3" /> : <UserX className="w-3 h-3" />}
                          {dealer.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {!dealer.is_admin && (
                          <button
                            onClick={() => handleToggleDealer(dealer.id)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                              dealer.is_active
                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                          >
                            {dealer.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
